export function getPlanVersionInformationQuery (providerId, planVersionId) {
  return JSON.stringify({
    query: `query GetPlanVersion {
      getPlanVersion(
        planVersionId: "${planVersionId}"
        providerId: "${providerId}"
      ) {
        ... on PlanVersionNotFound {
          errorMessage
          errorCode
          planVersionId
          providerId
        }
        ... on PlanVersion {
          providerId
          planServices {
            providerId
            balanceName
            balanceTypeIds
            managedBalanceTypeId
            id
            name
            ratingGroupId
            priority
            providedFields {
              name
              dataType
              usedByRules {
                edges {
                  node {
                    providerId
                    name
                    id
                  }
                }
              }
              customData
            }
            ruleSets {
              charging {
                id
                name
                pluginId
              }
              crediting {
                id
                name
                pluginId
              }
              recurring {
                id
                name
                pluginId
              }
            }
          }
          template {
            ... on InitialTemplateInstance {
              services {
                ratingGroupId
                priority
                unit{
                  periodAllowance
                  balanceName
                }
              }
              firstUsageFee
              purchaseFee
              period {
                periodType
                numberOfPeriods
                recurring
              }
            }
          }
        }
      }
    }
    `
  })
}

export function getMyProviderConfig () {
  return JSON.stringify({
    query: `query GetMyProviderConfig {
        getMyProviderConfig {
          ... on ProviderConfig {
            id
            allowedLifecycleTransitions
            commonName
            deleteProviderExecutionArn
            emergencyNumbers
            eventBusArn
            homeNetworks
            lifecycleStage
            limits {
              apiType
              hardLimitTps
              overHardLimitUntil
              overSoftLimitUntil
              rejectRequestsOverSoftLimit
              softLimitTps
            }
            name
          }
          ... on InvalidProviderLifecycleStage {
            errorCode
            errorMessage
            providerLifecycleStage
          }
          ... on RateLimitExceeded {
            errorCode
            errorMessage
            retryAfter
          }
          __typename
        }
      }`
  })
}

export function getSettingsQuery (providerId) {
  return JSON.stringify({
    query: `query GetSettings {
      getSettings(providerId: "${providerId}") {
        ... on Settings {
          providerId
          json
        }
      }
    }`
  })
}

export function subscribeToPlanQuery (accountId, providerId, planVersionId) {
  return JSON.stringify({
    query: `mutation SubscribeToPlan {
      subscribeToPlan(
        input: {
          accountId: "${accountId}"
          providerId: "${providerId}"
          planVersionId: "${planVersionId}"
        }
      ) {
        ... on SubscribeToPlanVersionValidationFailed {
          errorMessage
          errorCode
        }
        ... on InvalidField {
          errorMessage
          fieldName
          errorCode
        }
        ... on AccountNotFound {
          errorMessage
          providerId
          errorCode
          accountId
        }
        ... on PlanVersionIsNotAssignable {
          errorMessage
          providerId
          planVersionId
          errorCode
        }
        ... on PlanVersionNotFound {
          errorMessage
          planVersionId
          providerId
          errorCode
        }
        ... on SubscribeToPlanVersionPayload {
          __typename
          subscribedPlanVersion {
            to
            from
            planVersion {
              version
              state
              id
            }
          }
          account {
            customData
            id
            activePlanVersions {
              from
              to
              planVersion {
                version
                state
                id
              }
            }
            archivedPlanVersions {
              to
              from
              planVersion {
                version
                state
                id
              }
            }
            balance {
              version
              value
              customData
            }
            inactivePlanVersions {
              to
              planVersion {
                state
                version
                id
              }
              from
            }
          }
        }
      }
    }
    `
  })
}

export function createAccountQuery (accountId, providerId) {
  return JSON.stringify({
    query: `mutation CreateAccount {
      createAccount(
        input: { accountId: "${accountId}", providerId: "${providerId}" }
      ) {
        ... on InvalidField {
          errorMessage
          fieldName
          errorCode
        }
        ... on AccountAlreadyExists {
          errorMessage
          errorCode
          accountId
        }
        ... on AccountNotFound {
          errorMessage
          accountId
          errorCode
          providerId
        }
        ... on CreateAccountPayload {
          __typename
          account {
            id
            providerId
          }
        }
      }
    }
    `
  })
}

export function deleteAccountQuery (accountId, providerId) {
  return JSON.stringify({
    query: `mutation DeleteAccount {
      deleteAccount(
        input: { accountId: "${accountId}", providerId: "${providerId}" }
      ) {
        ... on AccountHasReferences {
          errorMessage
          providerId
          errorCode
          accountId
        }
        ... on AccountNotFound {
          errorMessage
          providerId
          accountId
          errorCode
        }
        ... on DeleteAccountPayload {
          __typename
          deletedAccountId
          parentAccount {
            providerId
            id
            friendsAndFamily
            customData
          }
        }
      }
    }
    `
  })
}

export function createDeviceQuery (deviceId, accountId, providerId) {
  return JSON.stringify({
    query: `mutation CreateDevice {
      createDevice(
        input: {
          accountId: "${accountId}"
          providerId: "${providerId}"
          deviceId: "${deviceId}"
        }
      ) {
        ... on CreateDevicePayload {
          __typename
          device {
            providerId
            id
            customData
            account {
              id
              providerId
            }
          }
        }
        ... on DeviceAlreadyExists {
          errorMessage
          errorCode
          deviceId
        }
        ... on AccountNotFound {
          errorMessage
          providerId
          errorCode
          accountId
        }
        ... on InvalidField {
          errorMessage
          fieldName
          errorCode
        }
      }
    }
    `
  })
}

export function deleteDeviceQuery (deviceId, accountId, providerId) {
  return JSON.stringify({
    query: `mutation DeleteDevice {
      deleteDevice(
        input: {
          accountId: "${accountId}"
          providerId: "${providerId}"
          deviceId: "${deviceId}"
        }
      ) {
        ... on DeviceNotFound {
          errorMessage
          providerId
          errorCode
          deviceId
        }
        ... on DeleteDevicePayload {
          __typename
          deletedDeviceId
          account {
            providerId
            id
          }
        }
      }
    }
    `
  })
}

export function getDeviceQuery (deviceId, providerId) {
  return JSON.stringify({
    query: `query GetDevice {
      getDevice(
        providerId: "${providerId}",
        deviceId: "${deviceId}"
      ) {
        ... on Device {
          providerId
          id
          account {
            providerId
            id
            customData
            churnScore
            creditLimit
            parent {
              providerId
              id
              activatedAt
            }
            activePlanVersions {
              planSubscriptionId
              from
              to
              overrides {
                name
                value
              }
              planVersion {
                providerId
                id
                version
                createdAt
                modifiedAt
              }
            }
            friendsAndFamily
            activatedAt
          }
          customData
        }
        ... on DeviceNotFound {
          providerId
          deviceId
          errorCode
          errorMessage
        }
        ... on InvalidProviderLifecycleStage {
          providerLifecycleStage
          errorCode
          errorMessage
        }
        ... on RateLimitExceeded {
          retryAfter
          errorCode
          errorMessage
        }
      }
    }
    `
  })
}

export function getPlansQuery (providerId, after) {
  const variables = {
    orderBy: {
      direction: 'DESC',
      field: 'LAST_MODIFICATION_DATE'
    }
  }
  if (after) {
    variables.after = after
  }
  return JSON.stringify({
    query: `query GetPlans($orderBy: PlanOrder, $after: String) {
      getPlans(first: 25, providerId: "${providerId}", orderBy: $orderBy, after: $after) {
        edges {
          node {
            id
            name
            versions(first: 100) {
              edges {
                node {
                  version
                  state
                  id
                  createdAt
                  modifiedAt
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }`,
    variables
  })
}

export function getPlanQuery (providerId, planId) {
  return JSON.stringify({
    query: `query GetPlan {
      getPlan(
        providerId: "${providerId},
        planId: "${planId}"
      ) {
        ... on Plan {
          providerId
          id
          name
        }
        ... on PlanNotFound {
          errorMessage
          providerId
          errorCode
          planId
        }
        ... on InvalidProviderLifecycleStage {
          providerLifecycleStage
          errorCode
          errorMessage
        }
        ... on RateLimitExceeded {
          retryAfter
          errorCode
          errorMessage
        }
      }
    }`
  })
}

export function getAccountActivePlansQuery (accountId, providerId) {
  return JSON.stringify({
    query: `
        query GetAccount {
            getAccount(accountId: "${accountId}", providerId: "${providerId}") {
            ... on AccountNotFound {
              errorMessage
              providerId
              errorCode
              accountId
            }
            ... on Account {
              id
              providerId
              churnScore
              creditLimit
              activePlanVersions {
                planSubscriptionId
                from
                to
                overrides {
                  name
                  value
                }
                planVersion {
                  createdAt
                  createdBy
                  deployedAt
                  deployedBy
                  id
                  modifiedAt
                  modifiedBy
                  priority
                  refCount
                  state
                  version
                  planServices {
                    providerId
                    balanceName
                    balanceTypeIds
                    managedBalanceTypeId
                    id
                    name
                    ratingGroupId
                    priority
                    providedFields {
                      name
                      dataType
                      usedByRules {
                        edges {
                          node {
                            providerId
                            name
                            id
                          }
                        }
                      }
                      customData
                    }
                    ruleSets {
                      charging {
                        id
                        name
                        pluginId
                      }
                      crediting {
                        id
                        name
                        pluginId
                      }
                      recurring {
                        id
                        name
                        pluginId
                      }
                    }
                  }
                }
              }
            }
          }
        }
        `
  })
}

export function getAccountQuery (accountId, providerId) {
  return JSON.stringify({
    query: `
        query GetAccount {
            getAccount(accountId: "${accountId}", providerId: "${providerId}") {
            ... on AccountNotFound {
              errorMessage
              providerId
              errorCode
              accountId
            }
            ... on Account {
              id
              customData
              providerId
              churnScore
              creditLimit
              balance {
                customData
                value
                version
              }
              activePlanVersions {
                planSubscriptionId
                from
                to
                overrides {
                  name
                  value
                }
                planVersion {
                  createdAt
                  createdBy
                  deployedAt
                  deployedBy
                  id
                  modifiedAt
                  modifiedBy
                  priority
                  refCount
                  state
                  version
                  plan {
                    providerId
                    name
                    id
                  }
                  planServices {
                    providerId
                    balanceName
                    balanceTypeIds
                    managedBalanceTypeId
                    id
                    name
                    ratingGroupId
                    priority
                    providedFields {
                      name
                      dataType
                      usedByRules {
                        edges {
                          node {
                            providerId
                            name
                            id
                          }
                        }
                      }
                      customData
                    }
                    ruleSets {
                      charging {
                        id
                        name
                        pluginId
                      }
                      crediting {
                        id
                        name
                        pluginId
                      }
                      recurring {
                        id
                        name
                        pluginId
                      }
                    }
                  }
                }
              }
              archivedPlanVersions {
                from
                to
                overrides {
                  name
                  value
                }
                planVersion {
                  createdAt
                  createdBy
                  deployedAt
                  deployedBy
                  id
                  modifiedAt
                  modifiedBy
                  priority
                  refCount
                  state
                  version
                  plan {
                    providerId
                    name
                    id
                  }
                  planServices {
                    providerId
                    balanceName
                    balanceTypeIds
                    managedBalanceTypeId
                    id
                    name
                    ratingGroupId
                    priority
                    providedFields {
                      name
                      dataType
                      usedByRules {
                        edges {
                          node {
                            providerId
                            name
                            id
                          }
                        }
                      }
                      customData
                    }
                    ruleSets {
                      charging {
                        id
                        name
                        pluginId
                      }
                      crediting {
                        id
                        name
                        pluginId
                      }
                      recurring {
                        id
                        name
                        pluginId
                      }
                    }
                  }
                }
              }
              inactivePlanVersions {
                from
                to
                planVersion {
                  createdAt
                  createdBy
                  deployedAt
                  deployedBy
                  id
                  modifiedAt
                  modifiedBy
                  priority
                  refCount
                  state
                  version
                  planServices {
                    providerId
                    balanceName
                    balanceTypeIds
                    managedBalanceTypeId
                    id
                    name
                    ratingGroupId
                    priority
                    providedFields {
                      name
                      dataType
                      usedByRules {
                        edges {
                          node {
                            providerId
                            name
                            id
                          }
                        }
                      }
                      customData
                    }
                    ruleSets {
                      charging {
                        id
                        name
                        pluginId
                      }
                      crediting {
                        id
                        name
                        pluginId
                      }
                      recurring {
                        id
                        name
                        pluginId
                      }
                    }
                  }
                }
              }
            }
          }
        }
        `
  })
}

export function creditAccountMutation (accountId, providerId, amount) {
  return JSON.stringify({
    query: `mutation credit {
      creditAccount(
        input: { accountId: "${accountId}", providerId: "${providerId}", amount: ${amount} }
      ) {
        ... on InvalidField {
          errorMessage
          fieldName
          errorCode
        }
        ... on CreditAccountPayload {
          __typename
          account {
            id
            providerId
            balance {
              customData
            }
          }
        }
      }
    }
    `
  })
}

export function getEventDataRecordsByDeviceQuery (providerId, deviceId, timestampStart, timestampEnd, after) {
  let query
  query = `query getEventDataRecordsByDevice {
    getEventDataRecordsByDevice(
      providerId: "${providerId}",
      deviceId: "${deviceId}",`
  if (after !== undefined) {
    query += `after: "${after}",`
  }
  query += `first: 10,
      filterBy: {
        types: [BILLING, CHARGING],
        from: "${timestampStart}",
        to: "${timestampEnd}"
    })
    {
        ... on EventDataRecordDeviceConnection{
              edges{
                  node{
                      providerId
                      action
                      eventData
                      customData
                      createdAt
                      id
                  }
                  cursor
              }
              pageInfo{
              hasPreviousPage
              hasNextPage
              startCursor
              endCursor
              }
          }
          ... on DeviceNotFound{
              providerId
              deviceId
              errorCode
              errorMessage
          }
      }
    }`
  return JSON.stringify({
    query: query
  })
}
export function CancelPlanSubscriptionByPlanVersionMutation (accountId, providerId, planVersionId) {
  return JSON.stringify({
    query: `mutation CancelPlanSubscription {
      cancelPlanSubscription(
        input: {
          accountId: "${accountId}"
          providerId: "${providerId}"
          planVersionId: "${planVersionId}"
        }
      ) {
        ... on SubscriptionNotFound {
          from
          to
          planVersionId
          errorMessage
          errorCode
        }
        ... on InvalidField {
          errorMessage
          fieldName
          errorCode
        }
        ... on AccountNotFound {
          errorMessage
          providerId
          errorCode
          accountId
        }
        ... on PlanVersionNotFound {
          providerId
          planVersionId
          errorMessage
          errorCode
        }
        ... on InternalServerError {
          errorCode
          errorMessage
        }
        ... on InvalidProviderLifecycleStage {
          errorCode
          errorMessage
          providerLifecycleStage
        }
        ... on MultipleSubscriptions {
          planVersionId
          errorCode
          errorMessage
        }
        ... on CancelPlanVersionSubscriptionPayload {
          __typename
          account {
            providerId
            id
            customData
            activePlanVersions {
              to
              from
              planVersion {
                version
                state
                id
              }
            }
            archivedPlanVersions {
              to
              from
              planVersion {
                version
                state
                id
              }
            }
            inactivePlanVersions {
              to
              from
              planVersion {
                version
                state
                id
              }
            }
          }
        }
      }
    }
    `
  })
}

export function CancelPlanSubscriptionByPlanSubscriptionMutation (accountId, providerId, planSubscriptionId) {
  return JSON.stringify({
    query: `mutation CancelPlanSubscription {
      cancelPlanSubscription(
        input: {
          accountId: "${accountId}"
          providerId: "${providerId}"
          planSubscriptionId: "${planSubscriptionId}"
        }
      ) {
        ... on SubscriptionNotFound {
          from
          to
          planVersionId
          errorMessage
          errorCode
        }
        ... on InvalidField {
          errorMessage
          fieldName
          errorCode
        }
        ... on AccountNotFound {
          errorMessage
          providerId
          errorCode
          accountId
        }
        ... on PlanVersionNotFound {
          providerId
          planVersionId
          errorMessage
          errorCode
        }
        ... on InternalServerError {
          errorCode
          errorMessage
        }
        ... on InvalidProviderLifecycleStage {
          errorCode
          errorMessage
          providerLifecycleStage
        }
        ... on MultipleSubscriptions {
          planVersionId
          errorCode
          errorMessage
        }
        ... on CancelPlanVersionSubscriptionPayload {
          __typename
          account {
            providerId
            id
            customData
            activePlanVersions {
              to
              from
              planVersion {
                version
                state
                id
              }
            }
            archivedPlanVersions {
              to
              from
              planVersion {
                version
                state
                id
              }
            }
            inactivePlanVersions {
              to
              from
              planVersion {
                version
                state
                id
              }
            }
          }
        }
      }
    }
    `
  })
}

export function getCurrentUserQuery () {
  return JSON.stringify({
    query: `query GetCurrentUser {
      getCurrentUser {
        providerId
        userId
        email
        name
        roleGroupMemberships
        phoneNumber
        jobTitle
        softwareMfaEnabled
      }
    }`
  })
}

export function updateProviderConfigEventBusArn (eventBusArn) {
  return JSON.stringify({
    query: `mutation updateMyProviderConfig {
      updateMyProviderConfig(
        input: {
          eventBusArn: "${eventBusArn}"
        }
      ) {
        ... on ProviderConfig {
          commonName
          eventBusArn
          homeNetworks
          emergencyNumbers
        }
        ... on InvalidProviderLifecycleStage {
          errorCode
          errorMessage
          providerLifecycleStage
        }
        ... on InvalidField {
          fieldName
          errorCode
          errorMessage
        }
      }
    }`
  })
}

export function getFieldMappingsQuery (providerId, first) {
  return JSON.stringify({
    query: `query GetFieldMappings {
      getFieldMappings(
        providerId: "${providerId}",
        first: ${first}
      ) {
        ... on FieldMappingConnection {
          edges {
            node {
              path
              expr
              field {
                name
                dataType
                customData
              }
            }
            cursor
          }
          pageInfo {
            endCursor
            hasPreviousPage
            hasNextPage
          }
        }
      }
    }`
  })
}
